/* eslint-disable no-param-reassign */
import { NavigateFunction } from 'react-router-dom';

import { createAsyncThunk } from '@reduxjs/toolkit';

import { BinType } from 'commons/enums';
import {
  PRIMARY_BLUE,
  PRIMARY_GREEN,
  PRIMARY_RED,
} from 'commons/styles/colors';
import {
  PickingListBySKUs,
  PickingRecommendation,
  PickingTask,
  PickingTaskValidation,
} from 'commons/types';
import { defaultActionProcess } from 'commons/utils/defaultActionProcess';
import { RootReducerInterface } from 'redux-stores/reducers';
import {
  addAssignedTask,
  removeAssignedTask,
  setAssignedTask,
  setPickItemBinRecommendationInfo,
  updateAssignedTask,
  updateInvalidSKUs,
  updateTaskBySKU,
  updateTaskBySKUBarcodeScanned,
  updateTaskBySKUPickRecommendation,
} from 'redux-stores/reducers/pickingTaskReducer';
import { snackbarSetData } from 'redux-stores/reducers/utilityReducer';
import { AppDispatch } from 'redux-stores/store';
import { PICK_TASK_ROUTES } from 'routes/constants';
import { swipeRxWmsApiAssignBasket } from 'services/apis/SwipeRxWmsApiAssignBasket';
import { BasketSizeEnum } from 'services/apis/SwipeRxWmsApiAssignBasket/interfaces';
import { swiperxWmsApiPickJob } from 'services/apis/SwipeRxWmsApiPickJob';
import {
  IPickingJobPickedItemRequest,
  IPickingJobPickedItemVerificationRequest,
  IPickingJobProductDetailRequest,
  IPickingJobProductDetailResponse,
  IPickingJobRecommendationRequest,
  IPickingJobRemoveBatchRequest,
  IPickingJobVerifyBarcodeRequest,
} from 'services/apis/SwipeRxWmsApiPickJob/types';
import { swipeRxWmsApiPickList } from 'services/apis/SwipeRxWmsApiPickList';
import { IAssignBasketRequest } from 'services/apis/SwipeRxWmsApiPickList/interfaces';

/* eslint-disable @typescript-eslint/no-unused-vars */
export const getPickingTasksAction = createAsyncThunk(
  'pickingTask/getPickingTaskAction',
  async (_, { dispatch }) => {
    defaultActionProcess(async () => {
      const data = await swipeRxWmsApiPickList.getPicklists();
      dispatch(setAssignedTask(data));
    }, dispatch);
    return undefined;
  },
);

export const actionSetAssignedTask = createAsyncThunk(
  'pickingTask/actionSetAssignedTask',
  async (_, { dispatch }) => {
    defaultActionProcess(async () => {
      const data = await swipeRxWmsApiPickList.assignPickingTask();
      dispatch(setAssignedTask(data));
    }, dispatch);
    return undefined;
  },
);

export const actionAddAssignedTask = createAsyncThunk(
  'pickingTask/actionAddAssignedTask',
  async (_, { dispatch, getState }) => {
    await defaultActionProcess(async () => {
      const state: RootReducerInterface = getState() as RootReducerInterface;
      const data = await swipeRxWmsApiPickList.addPickingTask({
        order_type: state.pickingTask.assignedTasks[0].po_type,
      });

      dispatch(addAssignedTask(data));
    }, dispatch);
    return undefined;
  },
);

export const actionRemoveAssignedTask = createAsyncThunk(
  'pickingTask/actionRemoveAssignedTask',
  async (poNumber: string, { dispatch }) => {
    defaultActionProcess(async () => {
      const data = await swipeRxWmsApiPickList.removePickingTask(poNumber);
      dispatch(removeAssignedTask(data));
    }, dispatch);
    return undefined;
  },
);

// called without using dispatch
export const actionStartPickingTask = createAsyncThunk(
  'pickingTask/startPickingTask',
  async (
    payload: { tasks: PickingTask[]; navigate: NavigateFunction },
    { dispatch },
  ) => {
    defaultActionProcess(async () => {
      const data = await swipeRxWmsApiPickList.startPickingProcess(
        payload.tasks.map((task) => task.po_number),
      );
      if (data !== 'ok') {
        dispatch(
          snackbarSetData({
            open: true,
            message: data,
            color: PRIMARY_BLUE,
          }),
        );
      }
      payload.navigate(PICK_TASK_ROUTES.LIST);
    }, dispatch);
    return undefined;
  },
);

export const assignBasket = createAsyncThunk(
  'pickingTask/assignBasket',
  async (params: IAssignBasketRequest, { dispatch }) => {
    const { task, updateState } = params;
    defaultActionProcess(async () => {
      const payload = { po_number: task.po_number, baskets: task.baskets };
      await swipeRxWmsApiAssignBasket.assignBasketsToAPI(payload);
      if (updateState) dispatch(updateAssignedTask(task as PickingTask));
    }, dispatch);
  },
);

export const getBasketRecommendation = async (
  poNumber: string,
): Promise<string> => {
  const result =
    await swipeRxWmsApiAssignBasket.getBasketRecommendation(poNumber);
  if (!result) return '';

  const recommendations: string[] = [];
  const sizes = Object.values(BasketSizeEnum);
  for (let i = 0; i < sizes.length; i += 1) {
    if (result[sizes[i]] > 0) {
      recommendations.push(`${sizes[i].charAt(0)}: ${result[sizes[i]]}`);
    }
  }

  return recommendations.join(', ');
};

export const getPickTaskBySKU = createAsyncThunk(
  'pickingTask/getPickTaskBySKU',
  async (_, { dispatch, getState }) => {
    defaultActionProcess(async () => {
      const response: PickingListBySKUs[] =
        await swiperxWmsApiPickJob.getPickJobs();

      const currentState = getState() as RootReducerInterface;
      const { invalidSKUs } = currentState.pickingTask;
      const invalidSKUList: string[] = Object.keys(invalidSKUs);

      // sort invalid SKUs first
      const sortedTasksBySKU = response.sort((sku1, sku2) => {
        const sku1IsInvalid = invalidSKUList.includes(sku1.sku_code);
        const sku2IsInvalid = invalidSKUList.includes(sku2.sku_code);

        if (sku1IsInvalid && !sku2IsInvalid) return -1;
        if (!sku1IsInvalid && sku2IsInvalid) return 1;
        return 0;
      });

      dispatch(updateTaskBySKU(sortedTasksBySKU));
    }, dispatch);
  },
);

/**
 * @deprecated might not be used in the future. Will preserve the endpoint for a while
 */
export const getRecommendation = createAsyncThunk(
  'pickingTask/getRecommendation',
  async (params: IPickingJobRecommendationRequest, { dispatch }) => {
    if (!params.skuCode) return;
    const response = await swiperxWmsApiPickJob.getRecommendation(params);
    let pickingInfoPayload: {
      sku_code: string;
      pick_infos?: PickingRecommendation[];
    };
    if (response && response.length > 0) {
      pickingInfoPayload = {
        sku_code: response[0].sku_code,
        pick_infos: response.map((data) => ({
          bin: data.bin,
          expiry_date: data.expiry_date.toString(),
          inventory_number: data.inventory_number,
          quantity: data.quantity,
          bin_type: data.bin_type,
          is_preferred: data.is_preferred,
        })),
      };
    } else {
      pickingInfoPayload = { sku_code: params.skuCode };
    }
    dispatch(updateTaskBySKUPickRecommendation(pickingInfoPayload));
  },
);

export const getPickItemBinRecommendationInfo = createAsyncThunk(
  'pickingTask/getPickItemBinRecommendationInfo',
  async (params: { skuCode: string; binType: BinType }, { dispatch }) => {
    const { skuCode, binType } = params;
    defaultActionProcess(async () => {
      const result = await swiperxWmsApiPickJob.getBinRecommendationInfo(
        skuCode,
        binType,
      );
      dispatch(setPickItemBinRecommendationInfo(result));
    }, dispatch);
  },
);

export const getProductDetail = async (
  params: IPickingJobProductDetailRequest,
  dispatch: AppDispatch,
): Promise<IPickingJobProductDetailResponse | null> => {
  const response = await swiperxWmsApiPickJob.getProductDetail(params);
  if (response && response.data) {
    return response.data;
  }
  if (response.error) {
    const errorMessage =
      response.error === true
        ? 'Failed to get product detail. Please make sure SKU, Bin, and Inventory Number is correct.'
        : response.error;
    dispatch(
      snackbarSetData({
        open: true,
        message: errorMessage,
        color: PRIMARY_RED,
      }),
    );
  }
  return null;
};

export const postPickedItem = async (
  params: IPickingJobPickedItemRequest,
  dispatch: AppDispatch,
): Promise<void> => {
  const response = await swiperxWmsApiPickJob.postPickedItem(params);
  if (!response) {
    dispatch(
      snackbarSetData({
        open: true,
        message:
          'Failed to get pick item. Please make sure SKU, Bin, Inventory Number, and Quantity is correct.',
        color: PRIMARY_RED,
      }),
    );
  }
};

export const postPickBasketVerification = async (
  params: IPickingJobPickedItemVerificationRequest,
  dispatch: AppDispatch,
): Promise<boolean> => {
  const response =
    await swiperxWmsApiPickJob.postPickedItemVerification(params);
  if (!response) {
    dispatch(
      snackbarSetData({
        open: true,
        message: 'Failed to verify pick item basket.',
        color: PRIMARY_RED,
      }),
    );
  }
  return response;
};

export const postBarcodeVerification = async (
  params: IPickingJobVerifyBarcodeRequest,
  dispatch: AppDispatch,
): Promise<boolean> => {
  try {
    await swiperxWmsApiPickJob.postBarcodeVerification(params);
    dispatch(updateTaskBySKUBarcodeScanned({ params, barcodeScanned: true }));
    dispatch(
      snackbarSetData({
        open: false,
      }),
    );
    return true;
  } catch (error: any) {
    dispatch(updateTaskBySKUBarcodeScanned({ params, barcodeScanned: false }));
    dispatch(
      snackbarSetData({
        open: true,
        color: PRIMARY_RED,
        message: error.message,
      }),
    );
    return false;
  }
};

export const postRemoveBatch = async (
  params: IPickingJobRemoveBatchRequest,
): Promise<void> => {
  await swiperxWmsApiPickJob.postRemoveBatch(params);
};

export const validatePickingList = (
  params: PickingListBySKUs[],
  dispatch: AppDispatch,
): PickingTaskValidation => {
  let validation: PickingTaskValidation = {
    validation: 'success',
  };
  const invalidSKUs: {
    [sku_code: string]: PickingTaskValidation;
  } = {};

  let isQuantityError = false;
  let isBasketError = false;

  params.forEach((pickItem) => {
    const {
      sku_code: skuCode,
      details,
      total_quantity: totalQuantity,
    } = pickItem;
    const currentQuantity = details.reduce((total, detail) => {
      const qty = detail.picked_actual.reduce(
        (pickedTotal, picked) => pickedTotal + picked.quantity,
        0,
      );
      return total + qty;
    }, 0);

    // get invalid baskets by comparing baskets with basket inside pick actual
    // remove duplicate basket data
    const invalidBaskets = details
      .flatMap((detail) =>
        detail.picked_actual
          .filter((pickActual) => !detail.baskets.includes(pickActual.basket))
          .map((pickActual) => {
            return {
              ...pickActual,
              po_number: detail.po_number,
            };
          }),
      )
      .filter(
        (basket, idx, baskets) =>
          baskets.map((b) => b.basket).indexOf(basket.basket) === idx,
      );

    if (totalQuantity > currentQuantity && validation.validation !== 'error') {
      validation = {
        validation: 'warning',
        message: 'Some SKUs is not yet picked',
      };
    } else if (totalQuantity < currentQuantity) {
      validation = {
        validation: 'error',
        message: `SKU ${skuCode} have incorrect quantity`,
      };
      isQuantityError = true;
      invalidSKUs[skuCode] = validation;
    }

    if (invalidBaskets.length > 0) {
      const incorrectQtyMessage = isQuantityError
        ? 'incorrect quantity & '
        : '';
      validation = {
        validation: 'error',
        invalidBaskets,
        message: `SKU ${skuCode} have ${incorrectQtyMessage} incorrect baskets ${invalidBaskets
          .map((basket) => basket.basket)
          .join(', ')}`,
      };
      isBasketError = true;
      invalidSKUs[skuCode] = validation;
    }
  });

  const snackbarErrorMessage = isQuantityError
    ? isBasketError
      ? 'Some SKUs have incorrect quantity & baskets'
      : 'Some SKUs have incorrect quantity'
    : isBasketError
    ? 'Some SKUs have incorrect baskets'
    : '';

  if (snackbarErrorMessage) {
    dispatch(
      snackbarSetData({
        open: true,
        message: snackbarErrorMessage,
        color: PRIMARY_RED,
      }),
    );
  }

  dispatch(updateInvalidSKUs(invalidSKUs));
  return validation;
};

export const postComplete = createAsyncThunk(
  'pickingTask/postComplete',
  async (params: { lineNumber: number; next?: () => void }, { dispatch }) => {
    return defaultActionProcess(async () => {
      await swiperxWmsApiPickJob.postCompletePick(params.lineNumber);
      dispatch(
        snackbarSetData({
          open: true,
          message: 'Picking Task Processed',
          color: PRIMARY_GREEN,
        }),
      );
      if (params.next) params.next();
    }, dispatch);
  },
);
